// src/hooks/useSttHealth.js
//
// Merouani1 §5 — Local STT health probe for the dashboard 💡 indicator.
//
// State machine (§5.2):
//   - grey  — probing (initial, or in flight)
//   - amber — /health answered, pipeline still warming (no tarteel run yet)
//   - green — /transcribe round-trip with tarteel succeeded
//   - red   — /health or /transcribe failed / timed out
//
// The hook re-probes every 5 minutes and on `window: online`. Each probe:
//   1. GET  ${url}/health  — cheap, confirms reachability and sets amber.
//   2. POST ${url}/transcribe with /audio/boot-al-fatiha.m4a + model=tarteel
//      — confirms the model round-trips, sets green.
// A 10-second timeout bounds each stage; failure lands red with the
// underlying error message so the diagnostic drawer can surface it.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_CLIP = '/audio/boot-al-fatiha.m4a';
const DEFAULT_MODEL = 'tarteel';
const DEFAULT_REPROBE_MS = 5 * 60 * 1000;
const DEFAULT_STAGE_TIMEOUT_MS = 10_000;

export const STT_HEALTH = Object.freeze({
  GREY: 'grey',
  AMBER: 'amber',
  GREEN: 'green',
  RED: 'red',
});

const withTimeout = (promise, ms, onAbort) => {
  let timer;
  // `settled` guards the timer callback: once the fetch has won the race we
  // must NOT abort the controller, otherwise the (already-resolved) response
  // gets aborted and the following body read (.json()/.blob()) — or the late
  // abort itself — rejects with "signal is aborted without reason". The race
  // resolves on a microtask, which runs before the setTimeout macrotask, so
  // the flag is reliably set before the timer can fire.
  let settled = false;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      if (settled) return;
      onAbort?.();
      reject(new Error(`STT probe timed out after ${ms} ms`));
    }, ms);
  });
  // When the timeout genuinely wins the race we abort `promise`, which then
  // rejects with an AbortError nobody is awaiting any more. Swallow that
  // orphaned rejection so it doesn't surface as an uncaught runtime error.
  promise.catch(() => {});
  return Promise.race([promise, timeout]).finally(() => {
    settled = true;
    clearTimeout(timer);
  });
};

export const probeSttHealth = async ({
  url,
  clipUrl = DEFAULT_CLIP,
  model = DEFAULT_MODEL,
  timeoutMs = DEFAULT_STAGE_TIMEOUT_MS,
  onStage,
  fetchImpl = typeof fetch !== 'undefined' ? fetch : null,
} = {}) => {
  if (!fetchImpl) {
    throw new Error('useSttHealth: no fetch implementation available');
  }
  if (!url) {
    throw new Error('REACT_APP_LOCAL_STT_URL is not set');
  }

  const started = performance?.now?.() ?? Date.now();
  const healthCtl = new AbortController();
  const healthResp = await withTimeout(
    fetchImpl(`${url.replace(/\/+$/, '')}/health`, {
      method: 'GET',
      signal: healthCtl.signal,
    }),
    timeoutMs,
    () => healthCtl.abort(),
  );
  if (!healthResp.ok) {
    throw new Error(`/health returned HTTP ${healthResp.status}`);
  }
  const health = await healthResp.json();
  onStage?.({ state: STT_HEALTH.AMBER, health });

  // Fetch the tiny boot clip the CRA build ships in public/audio/.
  const clipCtl = new AbortController();
  const clipResp = await withTimeout(
    fetchImpl(clipUrl, { signal: clipCtl.signal }),
    timeoutMs,
    () => clipCtl.abort(),
  );
  if (!clipResp.ok) {
    throw new Error(`boot clip fetch returned HTTP ${clipResp.status}`);
  }
  const clipBlob = await clipResp.blob();

  const form = new FormData();
  form.append('audio', clipBlob, 'boot-al-fatiha.m4a');
  form.append('model', model);

  const transcribeCtl = new AbortController();
  const transcribeResp = await withTimeout(
    fetchImpl(`${url.replace(/\/+$/, '')}/transcribe`, {
      method: 'POST',
      body: form,
      signal: transcribeCtl.signal,
    }),
    timeoutMs,
    () => transcribeCtl.abort(),
  );
  if (!transcribeResp.ok) {
    throw new Error(`/transcribe returned HTTP ${transcribeResp.status}`);
  }
  const transcribe = await transcribeResp.json();
  const latencyMs = Math.round((performance?.now?.() ?? Date.now()) - started);
  return { health, transcribe, latencyMs };
};

const useSttHealth = ({
  url = process.env.REACT_APP_LOCAL_STT_URL,
  clipUrl = DEFAULT_CLIP,
  model = DEFAULT_MODEL,
  reprobeMs = DEFAULT_REPROBE_MS,
  timeoutMs = DEFAULT_STAGE_TIMEOUT_MS,
  fetchImpl,
  autoStart = true,
} = {}) => {
  const [state, setState] = useState(STT_HEALTH.GREY);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const inflight = useRef(false);
  const mounted = useRef(true);

  // Dev-only warning when the env var is missing so the console makes
  // the config gap obvious (§5.3).
  useEffect(() => {
    if (!url && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(
        '[useSttHealth] REACT_APP_LOCAL_STT_URL is not set — indicator will stay red until the env var is provided and the dev server is restarted.',
      );
    }
  }, [url]);

  const run = useCallback(async () => {
    if (inflight.current) return;
    if (!url) {
      setState(STT_HEALTH.RED);
      setError('REACT_APP_LOCAL_STT_URL is not set');
      return;
    }
    inflight.current = true;
    setState(STT_HEALTH.GREY);
    setError(null);
    try {
      const result = await probeSttHealth({
        url,
        clipUrl,
        model,
        timeoutMs,
        fetchImpl,
        onStage: ({ state: stage, health }) => {
          if (!mounted.current) return;
          if (stage === STT_HEALTH.AMBER) {
            setState(STT_HEALTH.AMBER);
            setDetail({ health });
          }
        },
      });
      if (!mounted.current) return;
      setState(STT_HEALTH.GREEN);
      setDetail(result);
    } catch (e) {
      if (!mounted.current) return;
      setState(STT_HEALTH.RED);
      setError(e?.message || String(e));
    } finally {
      inflight.current = false;
    }
  }, [url, clipUrl, model, timeoutMs, fetchImpl]);

  useEffect(() => {
    mounted.current = true;
    if (autoStart) run();
    return () => {
      mounted.current = false;
    };
  }, [autoStart, run]);

  useEffect(() => {
    if (!reprobeMs || reprobeMs <= 0) return undefined;
    const id = setInterval(() => {
      if (!mounted.current) return;
      run();
    }, reprobeMs);
    return () => clearInterval(id);
  }, [reprobeMs, run]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onOnline = () => run();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [run]);

  return useMemo(
    () => ({ state, detail, error, refresh: run }),
    [state, detail, error, run],
  );
};

export default useSttHealth;
